import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { adminGetContentByType, adminCreateContent, adminUpdateContent, adminDeleteContent, adminListMedia, adminReorderContent } from '../../api/adminApi';
import { ContentDocument, TranslationContent } from '../../api/contentApi';
import RichTextEditor from '../../components/RichTextEditor';

const SUPPORTED_LANGS = ['de', 'fr', 'en'] as const;
const LANG_LABELS: Record<string, string> = { de: 'Deutsch', fr: 'Français', en: 'English' };

// Fields to show per content type
const FIELDS_BY_TYPE: Record<string, Array<{ key: keyof TranslationContent; label: string; type: 'text' | 'textarea' | 'richtext' | 'list' | 'media-url'; hint?: string }>> = {
  portrait: [
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'title', label: 'Titel / Title', type: 'text' },
    { key: 'introText', label: 'Intro Text', type: 'textarea' },
    { key: 'biography', label: 'Biografie', type: 'richtext' },
    { key: 'areasOfExpertise', label: 'Tätigkeitsschwerpunkte (je Zeile)', type: 'list' },
    { key: 'languages', label: 'Sprachen (je Zeile)', type: 'list' },
    { key: 'ctaText', label: 'CTA Button Text', type: 'text' },
  ],
  faq: [
    { key: 'question', label: 'Frage', type: 'text' },
    { key: 'answer', label: 'Antwort', type: 'richtext' },
  ],
  glossary: [
    { key: 'term', label: 'Begriff', type: 'text' },
    { key: 'explanation', label: 'Erklärung', type: 'richtext' },
  ],
  blog: [
    { key: 'title', label: 'Titel', type: 'text' },
    { key: 'slug', label: 'Slug (URL)', type: 'text' },
    { key: 'excerpt', label: 'Kurztext / Excerpt', type: 'textarea' },
    { key: 'content', label: 'Inhalt', type: 'richtext', hint: 'Glossar-Verlinkung: Über "+ Glossar"-Button einfügen → <span data-glossary-id="ID">Begriff</span>. FAQ-Verlinkung: Über "+ FAQ"-Button einfügen.' },
    { key: 'seoTitle', label: 'SEO Titel', type: 'text' },
    { key: 'seoDescription', label: 'SEO Beschreibung', type: 'text' },
    { key: 'disclaimer', label: 'Disclaimer (wird am Ende des Beitrags angezeigt)', type: 'richtext' },
  ],
  impressum: [
    { key: 'companyName', label: 'Firmenname', type: 'text' },
    { key: 'address', label: 'Adresse', type: 'textarea' },
    { key: 'phone', label: 'Telefon', type: 'text' },
    { key: 'email', label: 'E-Mail', type: 'text' },
    { key: 'registrationNumber', label: 'Registernummer', type: 'text' },
    { key: 'additionalHtml', label: 'Zusätzliche Informationen', type: 'richtext' },
    { key: 'disclaimer', label: 'Disclaimer (wird unter #disclaimer verlinkt von Blogbeiträgen)', type: 'richtext' },
  ],
  settings: [
    { key: 'urlPrivacy', label: 'Datenschutzerklärung URL', type: 'media-url' },
    { key: 'urlTerms', label: 'AGB URL', type: 'media-url' },
    { key: 'urlCancellation', label: 'Widerrufsrecht URL', type: 'media-url' },
  ],
};

const emptyTranslation = (): TranslationContent => ({ isPublished: true });

const AdminContentEditor: React.FC = () => {
  const { contentType } = useParams<{ contentType: string }>();
  const type = contentType || 'portrait';

  const [items, setItems] = useState<ContentDocument[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editDoc, setEditDoc] = useState<ContentDocument | null>(null);
  const [activeLang, setActiveLang] = useState<string>('de');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaPickerField, setMediaPickerField] = useState<keyof TranslationContent | null>(null);
  const [mediaPickerItems, setMediaPickerItems] = useState<Array<{ name: string; url: string; contentType: string; size: number }>>([]);
  const [mediaPickerLoading, setMediaPickerLoading] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [reordering, setReordering] = useState(false);

  // Blog content pickers — glossary term insertion + FAQ deep-link insertion
  const [glossaryEntries, setGlossaryEntries] = useState<ContentDocument[]>([]);
  const [faqEntries, setFaqEntries] = useState<ContentDocument[]>([]);
  const [glossaryPickerOpen, setGlossaryPickerOpen] = useState(false);
  const [faqPickerOpen, setFaqPickerOpen] = useState(false);
  const [pickerEditor, setPickerEditor] = useState<any>(null);
  const [pickerSearch, setPickerSearch] = useState('');

  const fields = FIELDS_BY_TYPE[type] || [];

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminGetContentByType(type);
      setItems([...data].sort((a, b) => (b.sortOrder ?? 0) - (a.sortOrder ?? 0)));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    loadItems();
    setSelectedId(null);
    setEditDoc(null);
  }, [loadItems]);

  const selectItem = (item: ContentDocument) => {
    setSelectedId(item.id);
    setEditDoc(JSON.parse(JSON.stringify(item))); // deep clone
    setActiveLang('de');
    setSuccess('');
    setError('');
  };

  const createNew = () => {
    const newDoc: ContentDocument = {
      id: '',
      type,
      isPublished: true,
      sortOrder: items.length,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      translations: {
        de: emptyTranslation(),
        fr: emptyTranslation(),
        en: emptyTranslation(),
      },
    };
    setSelectedId('__new__');
    setEditDoc(newDoc);
    setActiveLang('de');
    setSuccess('');
    setError('');
  };

  const updateTranslationField = (lang: string, key: keyof TranslationContent, value: any) => {
    if (!editDoc) return;
    setEditDoc({
      ...editDoc,
      translations: {
        ...editDoc.translations,
        [lang]: {
          ...(editDoc.translations[lang] || emptyTranslation()),
          [key]: value,
        },
      },
    });
  };

  const updateDocField = (key: string, value: any) => {
    if (!editDoc) return;
    setEditDoc({ ...editDoc, [key]: value });
  };

  const handleSave = async () => {
    if (!editDoc) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if (selectedId === '__new__') {
        const created = await adminCreateContent(editDoc);
        setSuccess('Erstellt!');
        await loadItems();
        selectItem(created);
      } else {
        const updated = await adminUpdateContent(type, editDoc.id, editDoc);
        setSuccess('Gespeichert!');
        await loadItems();
        selectItem(updated);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editDoc || selectedId === '__new__') return;
    if (!window.confirm('Wirklich löschen?')) return;
    setSaving(true);
    try {
      await adminDeleteContent(type, editDoc.id);
      setSuccess('Gelöscht.');
      setSelectedId(null);
      setEditDoc(null);
      await loadItems();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openMediaPicker = async (field: keyof TranslationContent) => {
    setMediaPickerField(field);
    setMediaPickerOpen(true);
    setMediaPickerLoading(true);
    try {
      const items = await adminListMedia();
      setMediaPickerItems(items);
    } catch {
      setMediaPickerItems([]);
    } finally {
      setMediaPickerLoading(false);
    }
  };

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    const reordered = [...items];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(index, 0, moved);
    setItems(reordered);
    setDragIndex(index);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragIndex(null);
    setReordering(true);
    setError('');
    try {
      // Assign sortOrder: first item gets highest number so desc sort = visual order
      const reorderPayload = items.map((item, index) => ({
        id: item.id,
        sortOrder: items.length - index,
      }));
      await adminReorderContent(type, reorderPayload);
      // Update local state to reflect new sortOrders without a full reload
      const updatedItems = items.map((item, index) => ({
        ...item,
        sortOrder: items.length - index,
      }));
      setItems(updatedItems);
      if (editDoc) {
        const refreshed = updatedItems.find((u) => u.id === editDoc.id);
        if (refreshed) setEditDoc(JSON.parse(JSON.stringify(refreshed)));
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setReordering(false);
    }
  };

  const selectMediaItem = (url: string) => {
    if (mediaPickerField) {
      updateTranslationField(activeLang, mediaPickerField, url);
    }
    setMediaPickerOpen(false);
    setMediaPickerField(null);
  };

  const currentTranslation = editDoc?.translations?.[activeLang] || emptyTranslation();

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800 capitalize">{type}</h1>
        <button
          onClick={createNew}
          className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
        >
          + Neu erstellen
        </button>
      </div>

      {error && <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm">{error}</div>}
      {success && <div className="bg-green-50 text-green-700 p-3 rounded-lg mb-4 text-sm">{success}</div>}

      <div className="flex gap-6">
        {/* Items list */}
        <div className="w-64 shrink-0">
          {loading ? (
            <p className="text-gray-500 text-sm">Laden...</p>
          ) : items.length === 0 ? (
            <p className="text-gray-500 text-sm">Keine Einträge.</p>
          ) : (
            <div className="space-y-1">
              {reordering && <p className="text-xs text-gray-400 mb-1">Reihenfolge wird gespeichert…</p>}
              {items.map((item, index) => {
                const label = item.translations?.de?.title
                  || item.translations?.de?.name
                  || item.translations?.de?.question
                  || item.translations?.de?.term
                  || item.translations?.de?.companyName
                  || item.id;
                return (
                  <div
                    key={item.id}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={handleDrop}
                    className={`flex items-center gap-1 rounded cursor-grab active:cursor-grabbing ${
                      dragIndex === index ? 'opacity-50' : ''
                    }`}
                  >
                    <span className="text-gray-300 px-1 select-none">⠿</span>
                    <button
                      onClick={() => selectItem(item)}
                      className={`flex-1 text-left px-2 py-2 rounded text-sm transition ${
                        selectedId === item.id
                          ? 'bg-primary-100 text-primary-800 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <span className="block truncate">{label}</span>
                      <div className="flex gap-1 flex-wrap">
                        {!item.isPublished && (
                          <span className="text-xs text-orange-500">Entwurf</span>
                        )}
                        {item.isHighlighted && (
                          <span className="text-xs text-yellow-600">★ Highlight</span>
                        )}
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Editor */}
        {editDoc && (
          <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Top-level fields */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <label className="block">
                <span className="text-sm font-medium text-gray-700">Veröffentlicht</span>
                <select
                  value={editDoc.isPublished ? 'true' : 'false'}
                  onChange={(e) => updateDocField('isPublished', e.target.value === 'true')}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="true">Ja</option>
                  <option value="false">Nein (Entwurf)</option>
                </select>
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700">Sortierung</span>
                <input
                  type="number"
                  value={editDoc.sortOrder}
                  onChange={(e) => updateDocField('sortOrder', parseInt(e.target.value) || 0)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </label>
              {(type === 'faq' || type === 'glossary') && (
                <label className="block col-span-2">
                  <span className="text-sm font-medium text-gray-700">Kategorien (eine pro Zeile)</span>
                  <textarea
                    value={
                      editDoc.categories && editDoc.categories.length > 0
                        ? editDoc.categories.join('\n')
                        : editDoc.category || ''
                    }
                    onChange={(e) => {
                      const lines = e.target.value.split('\n').map((s: string) => s.trim()).filter((s: string) => s);
                      updateDocField('categories', lines);
                      updateDocField('category', lines[0] || '');
                    }}
                    rows={3}
                    placeholder="z.B. Vertragsrecht"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-y"
                  />
                </label>
              )}
              {type === 'blog' && (
                <>
                  <label className="flex items-center gap-2 col-span-2">
                    <input
                      type="checkbox"
                      checked={!!(editDoc as any).isHighlighted}
                      onChange={(e) => updateDocField('isHighlighted', e.target.checked)}
                      className="h-4 w-4 text-yellow-500 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">★ Als Highlight anzeigen (wird immer als Featured Post oben angezeigt)</span>
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700">Slug</span>
                    <input
                      type="text"
                      value={editDoc.slug || ''}
                      onChange={(e) => updateDocField('slug', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700">Autor</span>
                    <input
                      type="text"
                      value={(editDoc as any).author || ''}
                      onChange={(e) => updateDocField('author', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700">Veröffentlichungsdatum</span>
                    <input
                      type="date"
                      value={(editDoc as any).publicationDate || ''}
                      onChange={(e) => updateDocField('publicationDate', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700">Titelbild URL</span>
                    <input
                      type="text"
                      value={(editDoc as any).featuredImageUrl || ''}
                      onChange={(e) => updateDocField('featuredImageUrl', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="https://..."
                    />
                  </label>
                </>
              )}
              {type === 'portrait' && (
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Portrait Bild URL</span>
                  <input
                    type="text"
                    value={(editDoc as any).portraitImageUrl || ''}
                    onChange={(e) => updateDocField('portraitImageUrl', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="https://..."
                  />
                </label>
              )}
            </div>

            {/* Language tabs */}
            <div className="border-b border-gray-200 mb-6">
              <div className="flex gap-0">
                {SUPPORTED_LANGS.map((lng) => (
                  <button
                    key={lng}
                    onClick={() => setActiveLang(lng)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
                      activeLang === lng
                        ? 'border-primary-500 text-primary-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {LANG_LABELS[lng]}
                    {editDoc.translations?.[lng]?.isPublished === false && (
                      <span className="ml-1 text-xs text-orange-500">(Entwurf)</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Per-language published toggle */}
            <div className="mb-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={currentTranslation.isPublished}
                  onChange={(e) => updateTranslationField(activeLang, 'isPublished', e.target.checked)}
                  className="h-4 w-4 text-primary-500 rounded"
                />
                <span className="text-gray-700">
                  {LANG_LABELS[activeLang]} Version veröffentlicht
                </span>
              </label>
            </div>

            {/* Translation fields */}
            <div className="space-y-5">
              {fields.map((field) => {
                const value = (currentTranslation as any)?.[field.key] ?? '';

                if (field.type === 'text') {
                  return (
                    <label key={field.key} className="block">
                      <span className="text-sm font-medium text-gray-700">{field.label}</span>
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => updateTranslationField(activeLang, field.key, e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </label>
                  );
                }

                if (field.type === 'media-url') {
                  return (
                    <div key={field.key}>
                      <span className="text-sm font-medium text-gray-700 block mb-1">{field.label}</span>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => updateTranslationField(activeLang, field.key, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="https://..."
                        />
                        <button
                          type="button"
                          onClick={() => openMediaPicker(field.key)}
                          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm transition whitespace-nowrap"
                        >
                          Aus Medien
                        </button>
                      </div>
                    </div>
                  );
                }

                if (field.type === 'textarea') {
                  return (
                    <label key={field.key} className="block">
                      <span className="text-sm font-medium text-gray-700">{field.label}</span>
                      <textarea
                        value={value}
                        onChange={(e) => updateTranslationField(activeLang, field.key, e.target.value)}
                        rows={4}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-y"
                      />
                    </label>
                  );
                }

                if (field.type === 'richtext') {
                  const isBlogContent = type === 'blog' && field.key === 'content';
                  return (
                    <div key={field.key}>
                      <span className="text-sm font-medium text-gray-700 block mb-1">{field.label}</span>
                      <RichTextEditor
                        resetKey={`${selectedId}-${activeLang}-${field.key}`}
                        content={value}
                        onChange={(html) => updateTranslationField(activeLang, field.key, html)}
                        extraToolbar={isBlogContent ? (editor) => (
                          <>
                            <button
                              type="button"
                              className="px-2 py-1 text-sm rounded bg-primary-50 text-primary-700 hover:bg-primary-100 transition font-medium"
                              onClick={() => {
                              setPickerEditor(editor);
                              setPickerSearch('');
                              setGlossaryPickerOpen(true);
                              // Lazy-load entries on first open
                              if (glossaryEntries.length === 0) {
                                adminGetContentByType('glossary').then(setGlossaryEntries).catch(() => {});
                              }
                            }}
                            >
                              + Glossar
                            </button>
                            <button
                              type="button"
                              className="px-2 py-1 text-sm rounded bg-primary-50 text-primary-700 hover:bg-primary-100 transition font-medium"
                              onClick={() => {
                              setPickerEditor(editor);
                              setPickerSearch('');
                              setFaqPickerOpen(true);
                              // Lazy-load entries on first open
                              if (faqEntries.length === 0) {
                                adminGetContentByType('faq').then(setFaqEntries).catch(() => {});
                              }
                            }}
                            >
                              + FAQ
                            </button>
                          </>
                        ) : undefined}
                      />
                      {field.hint && (
                        <p className="mt-1 text-xs text-gray-400 font-mono whitespace-pre-wrap">{field.hint}</p>
                      )}
                    </div>
                  );
                }

                if (field.type === 'list') {
                  const listValue = Array.isArray(value) ? value.join('\n') : '';
                  return (
                    <label key={field.key} className="block">
                      <span className="text-sm font-medium text-gray-700">{field.label}</span>
                      <textarea
                        value={listValue}
                        onChange={(e) =>
                          updateTranslationField(activeLang, field.key, e.target.value.split('\n').filter((s: string) => s.trim()))
                        }
                        rows={4}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-y"
                      />
                    </label>
                  );
                }

                return null;
              })}
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50"
              >
                {saving ? 'Speichern...' : selectedId === '__new__' ? 'Erstellen' : 'Speichern'}
              </button>
              {selectedId !== '__new__' && (
                <button
                  onClick={handleDelete}
                  disabled={saving}
                  className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50"
                >
                  Löschen
                </button>
              )}
            </div>
          </div>
        )}

        {!editDoc && !loading && (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
            Wählen Sie einen Eintrag oder erstellen Sie einen neuen.
          </div>
        )}
      </div>
      {mediaPickerOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setMediaPickerOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800">Mediendatei wählen</h2>
              <button onClick={() => setMediaPickerOpen(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="overflow-y-auto flex-1 p-4">
              {mediaPickerLoading ? (
                <p className="text-gray-500 text-sm">Laden...</p>
              ) : mediaPickerItems.length === 0 ? (
                <p className="text-gray-500 text-sm">Keine Dateien vorhanden.</p>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {mediaPickerItems.map((item) => (
                    <button
                      key={item.name}
                      type="button"
                      onClick={() => selectMediaItem(item.url)}
                      className="text-left p-3 border border-gray-200 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition"
                    >
                      <p className="text-sm font-medium text-gray-700 truncate" title={item.name}>{item.name}</p>
                      <p className="text-xs text-gray-400 mt-1">{item.contentType}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Glossary term picker modal */}
      {glossaryPickerOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setGlossaryPickerOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800">Glossar-Eintrag verlinken</h2>
              <button onClick={() => setGlossaryPickerOpen(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="p-3 border-b border-gray-100">
              <input
                type="text"
                autoFocus
                placeholder="Suchen…"
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              />
            </div>
            <div className="overflow-y-auto flex-1 p-2">
              {glossaryEntries
                .filter((e) => {
                  const tr = e.translations?.[activeLang] || e.translations?.['de'];
                  const term = (tr?.term || '').toLowerCase();
                  return !pickerSearch || term.includes(pickerSearch.toLowerCase());
                })
                .sort((a, b) => {
                  const tA = (a.translations?.[activeLang] || a.translations?.['de'])?.term || '';
                  const tB = (b.translations?.[activeLang] || b.translations?.['de'])?.term || '';
                  return tA.localeCompare(tB);
                })
                .map((entry) => {
                  const tr = entry.translations?.[activeLang] || entry.translations?.['de'];
                  return (
                    <button
                      key={entry.id}
                      type="button"
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-primary-50 hover:text-primary-800 transition text-sm"
                      onClick={() => {
                        if (pickerEditor) {
                          if (pickerEditor.state.selection.empty) {
                            // No selection — insert term text then apply the mark
                            const displayText = tr?.term || entry.id;
                            pickerEditor.chain().focus()
                              .insertContent({
                                type: 'text',
                                text: displayText,
                                marks: [{ type: 'glossaryTerm', attrs: { 'data-glossary-id': entry.id } }],
                              })
                              .run();
                          } else {
                            // Apply mark to selected text
                            pickerEditor.chain().focus()
                              .setMark('glossaryTerm', { 'data-glossary-id': entry.id })
                              .run();
                          }
                        }
                        setGlossaryPickerOpen(false);
                      }}
                    >
                      <span className="font-medium">{tr?.term || entry.id}</span>
                      {!entry.isPublished && <span className="ml-2 text-xs text-orange-500">(Entwurf)</span>}
                      <span className="block text-xs text-gray-400 truncate">{tr?.explanation?.replace(/<[^>]+>/g, '').slice(0, 80)}</span>
                    </button>
                  );
                })}
              {glossaryEntries.length === 0 && (
                <p className="text-sm text-gray-400 p-3">Keine Glossar-Einträge gefunden.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* FAQ deep-link picker modal */}
      {faqPickerOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setFaqPickerOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800">FAQ-Frage verlinken</h2>
              <button onClick={() => setFaqPickerOpen(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="p-3 border-b border-gray-100">
              <input
                type="text"
                autoFocus
                placeholder="Suchen…"
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              />
            </div>
            <div className="overflow-y-auto flex-1 p-2">
              {faqEntries
                .filter((e) => {
                  const tr = e.translations?.[activeLang] || e.translations?.['de'];
                  const question = (tr?.question || '').toLowerCase();
                  return !pickerSearch || question.includes(pickerSearch.toLowerCase());
                })
                .map((entry) => {
                  const tr = entry.translations?.[activeLang] || entry.translations?.['de'];
                  return (
                    <button
                      key={entry.id}
                      type="button"
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-primary-50 hover:text-primary-800 transition text-sm"
                      onClick={() => {
                        if (pickerEditor) {
                          const href = `/${activeLang}/faq#faq-${entry.id}`;
                          if (pickerEditor.state.selection.empty) {
                            // No selection — insert link text then apply link mark
                            const linkText = tr?.question || entry.id;
                            pickerEditor.chain().focus()
                              .insertContent({ type: 'text', text: linkText, marks: [{ type: 'link', attrs: { href } }] })
                              .run();
                          } else {
                            // Apply link to selected text
                            pickerEditor.chain().focus()
                              .setLink({ href })
                              .run();
                          }
                        }
                        setFaqPickerOpen(false);
                      }}
                    >
                      <span className="font-medium">{tr?.question || entry.id}</span>
                      {!entry.isPublished && <span className="ml-2 text-xs text-orange-500">(Entwurf)</span>}
                      {entry.category && <span className="ml-2 text-xs text-gray-400">{entry.category}</span>}
                    </button>
                  );
                })}
              {faqEntries.length === 0 && (
                <p className="text-sm text-gray-400 p-3">Keine FAQ-Einträge gefunden.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContentEditor;
